// Port seam for the permission IPC calls (the hotkey/keyboard seam family;
// the accessibility settings opener joined with the bridge port):
// they live app-side,
// so `src/composition/hosts/dictation/dictation-permissions-wiring.ts` injects them at bootstrap.
// `PermissionState` is declared locally on purpose — the wiring file is where
// it meets the structurally identical `@/tauri` union. Consumers gate on
// `isTauri()` first, so non-Tauri runs never reach the unconfigured throw.

export type PermissionState = 'granted' | 'denied' | 'unknown';

export interface DictationPermissionsPorts {
  /** Current microphone state. Never prompts, so it is safe on mount or a timer. */
  readMicrophonePermission: () => Promise<PermissionState>;
  /** Prompts for microphone access if never asked; call only from a user click. */
  requestMicrophonePermission: () => Promise<boolean>;
  /** Opens System Settings → Privacy → Microphone. */
  openMicrophoneSettings: () => Promise<void>;
  /** Current macOS Accessibility (TCC) state. */
  checkAccessibilityPermission: () => Promise<PermissionState>;
  /**
   * Requests Accessibility permission — first call shows the macOS dialog,
   * later calls open System Settings → Privacy → Accessibility.
   */
  requestAccessibilityPermission: () => Promise<PermissionState>;
  /**
   * Opens System Settings → Privacy → Accessibility directly.
   * The onboarding permission-revoked toast uses it when re-requesting
   * would not show a dialog.
   */
  openAccessibilityPrivacySettings: () => Promise<void>;
}

let ports: DictationPermissionsPorts | null = null;

export function configureDictationPermissionsPorts(
  config: DictationPermissionsPorts
): void {
  ports = config;
}

export function getDictationPermissionsPorts(): DictationPermissionsPorts {
  if (!ports) {
    throw new Error(
      'Dictation permissions ports are not configured. The app must call ' +
        'configureDictationPermissionsPorts() at bootstrap (see ' +
        'src/composition/hosts/dictation/dictation-permissions-wiring.ts).'
    );
  }
  return ports;
}
